import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select } from '@/components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../core/apiClient';

interface Employee {
  id: string;
  code: string;
  name: string;
  department: string;
}

interface Absence {
  id: string;
  employeeId: string;
  employeeName: string;
  startDate: string;
  endDate: string;
  type: string;
  status: string;
  notes: string;
}

interface Holiday {
  id: string;
  name: string;
  date: string;
  type: string;
}

interface CalendarData {
  year: number;
  month: number;
  startDate: string;
  endDate: string;
  holidays: Holiday[];
  absences: Absence[];
  employees: Employee[];
}

const ABSENCE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  vacation: { bg: 'bg-blue-500', text: 'text-white', label: 'Vacances' },
  sick_leave: { bg: 'bg-red-500', text: 'text-white', label: 'Baixa IT' },
  personal: { bg: 'bg-green-500', text: 'text-white', label: 'Permis' },
  maternity: { bg: 'bg-purple-500', text: 'text-white', label: 'Maternitat' },
  paternity: { bg: 'bg-purple-400', text: 'text-white', label: 'Paternitat' },
  other: { bg: 'bg-gray-500', text: 'text-white', label: 'Altres' },
};

const MONTH_NAMES = [
  'Gener', 'Febrer', 'Marc', 'Abril', 'Maig', 'Juny',
  'Juliol', 'Agost', 'Setembre', 'Octubre', 'Novembre', 'Desembre'
];

export default function LaborCalendar() {
  const navigate = useNavigate();
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [_selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [_selectedDate, setSelectedDate] = useState<string>('');
  void _selectedEmployee; void _selectedDate; // May be used for future modal interactions
  const [formData, setFormData] = useState({
    employeeId: '',
    type: 'vacation',
    startDate: '',
    endDate: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCalendar();
  }, [year, month]);

  const loadCalendar = async () => {
    try {
      setLoading(true);
      const response = await api.get<{ success: boolean; data: CalendarData }>(`/calendar?year=${year}&month=${month}`);
      if (response.success && response.data) {
        setCalendarData(response.data);
      }
    } catch (error) {
      console.error('Error loading calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generar dies del mes
  const daysInMonth = useMemo(() => {
    const date = new Date(year, month - 1, 1);
    const days: Date[] = [];
    while (date.getMonth() === month - 1) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    return days;
  }, [year, month]);

  // Mapa de festius per data
  const holidayMap = useMemo(() => {
    const map = new Map<string, Holiday>();
    calendarData?.holidays.forEach(h => {
      const dateKey = h.date.split('T')[0];
      map.set(dateKey, h);
    });
    return map;
  }, [calendarData?.holidays]);

  // Mapa d'absencies per empleat i data
  const absenceMap = useMemo(() => {
    const map = new Map<string, Absence>();
    calendarData?.absences.forEach(a => {
      const start = new Date(a.startDate);
      const end = new Date(a.endDate);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = `${a.employeeId}-${d.toISOString().split('T')[0]}`;
        map.set(key, a);
      }
    });
    return map;
  }, [calendarData?.absences]);

  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const getCellClass = (employeeId: string, date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    const key = `${employeeId}-${dateStr}`;
    
    // Comprovar absència
    const absence = absenceMap.get(key);
    if (absence) {
      return ABSENCE_COLORS[absence.type]?.bg || 'bg-gray-400';
    }
    
    // Comprovar festiu
    if (holidayMap.has(dateStr)) {
      return 'bg-amber-400';
    }
    
    // Cap de setmana
    if (isWeekend(date)) {
      return 'bg-gray-200';
    }
    
    return 'bg-white hover:bg-gray-50';
  };

  const getCellTitle = (employeeId: string, date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    const key = `${employeeId}-${dateStr}`;
    
    const absence = absenceMap.get(key);
    if (absence) {
      return `${ABSENCE_COLORS[absence.type]?.label || absence.type}${absence.notes ? `: ${absence.notes}` : ''}`;
    }
    
    const holiday = holidayMap.get(dateStr);
    if (holiday) {
      return holiday.name;
    }
    
    return '';
  };

  const handleCellClick = (employeeId: string, date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    setSelectedEmployee(employeeId);
    setSelectedDate(dateStr);
    setFormData({
      employeeId,
      type: 'vacation',
      startDate: dateStr,
      endDate: dateStr,
      notes: '',
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.post('/absences', formData);
      setIsModalOpen(false);
      await loadCalendar();
    } catch (error) {
      console.error('Error creating absence:', error);
    } finally {
      setSaving(false);
    }
  };

  const prevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const nextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/rrhh')}
            title="Tornar enrere"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Calendar className="h-6 w-6" />
              Calendari Laboral
            </h1>
            <p className="text-muted-foreground">Vista global d&apos;absencies i festius</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="icon"
            onClick={prevMonth}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-semibold text-sm w-40 text-center">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <Button 
            variant="outline" 
            size="icon"
            onClick={nextMonth}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button onClick={() => navigate('/rrhh/holidays')}>
            Gestionar festius
          </Button>
        </div>
      </div>

      {/* Legend */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium">Llegenda:</span>
            {Object.entries(ABSENCE_COLORS).map(([key, value]) => (
              <div key={key} className="flex items-center gap-1">
                <div className={`w-4 h-4 rounded ${value.bg}`} />
                <span className="text-sm">{value.label}</span>
              </div>
            ))}
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-amber-400" />
              <span className="text-sm">Festiu</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-gray-200" />
              <span className="text-sm">Cap de setmana</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendar Grid */}
      <Card>
        <CardHeader>
          <CardTitle>
            {MONTH_NAMES[month - 1]} {year}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Carregant calendari...</div>
          ) : !calendarData || calendarData.employees.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No hi ha empleats actius</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-background border p-2 text-left min-w-[150px]">
                      Empleat
                    </th>
                    {daysInMonth.map((day) => (
                      <th 
                        key={day.toISOString()} 
                        className={`border p-1 text-center min-w-[32px] ${
                          isWeekend(day) ? 'bg-gray-100' : ''
                        } ${holidayMap.has(day.toISOString().split('T')[0]) ? 'bg-amber-100' : ''}`}
                      >
                        <div className="text-xs font-normal text-muted-foreground">
                          {['Dg', 'Dl', 'Dm', 'Dc', 'Dj', 'Dv', 'Ds'][day.getDay()]}
                        </div>
                        <div className="font-semibold">{day.getDate()}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {calendarData.employees.map((employee) => (
                    <tr key={employee.id}>
                      <td className="sticky left-0 bg-background border p-2 font-medium">
                        <div className="truncate max-w-[140px]" title={employee.name}>
                          {employee.name}
                        </div>
                        <div className="text-xs text-muted-foreground">{employee.code}</div>
                      </td>
                      {daysInMonth.map((day) => {
                        const cellClass = getCellClass(employee.id, day);
                        const cellTitle = getCellTitle(employee.id, day);
                        return (
                          <td 
                            key={day.toISOString()} 
                            className={`border p-0 cursor-pointer transition-colors ${cellClass}`}
                            title={cellTitle}
                            onClick={() => handleCellClick(employee.id, day)}
                          >
                            <div className="h-8" />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal per crear absència */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova absencia</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium mb-1">Empleat</label>
                <select
                  value={formData.employeeId}
                  onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                >
                  <option value="">Selecciona empleat</option>
                  {calendarData?.employees.map(e => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tipus</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                >
                  <option value="vacation">Vacances</option>
                  <option value="sick_leave">Baixa IT</option>
                  <option value="personal">Permis personal</option>
                  <option value="maternity">Maternitat</option>
                  <option value="paternity">Paternitat</option>
                  <option value="other">Altres</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Data inici</label>
                  <Input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Data fi</label>
                  <Input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes opcionals..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel·lar
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Desant...' : 'Desa'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
